import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  SITE_NAME,
  SOCIAL_LINKS,
  FOOTER_LINKS,
  FOOTER_EMAILS,
} from '../config/site';
import { ovKey } from '../pages/omniversail/i18n.jsx';
import {
  IconYoutube,
  IconInstagram,
  IconTwitter,
  IconWeixin,
  IconPatreon,
  IconFacebook,
  IconLinkedin,
  IconPinterest,
  IconReddit,
  IconWhatsapp,
  IconTelegram,
} from './SocialIcons';
import '../styles/Footer.css';

const SOCIAL_ICONS = {
  YouTube: IconYoutube,
  Instagram: IconInstagram,
  X: IconTwitter,
  WeChat: IconWeixin,
  Patreon: IconPatreon,
  Facebook: IconFacebook,
  LinkedIn: IconLinkedin,
  Pinterest: IconPinterest,
  Reddit: IconReddit,
  WhatsApp: IconWhatsapp,
  Telegram: IconTelegram,
};

/**
 * Footer — concise link map:
 *   Company  → About / Contact / Careers
 *   Pricing  → View Plans / Monthly / Annual
 *   Services → Language Learning / Curriculum / Prep / Colleges
 *   plus a click-to-reveal "Questions? Email us" block whose addresses are
 *   real mailto: links (and are copied to the clipboard on click).
 */
export default function Footer() {
  const { t } = useTranslation();
  const [showEmails, setShowEmails] = useState(false);
  const [copied, setCopied] = useState('');

  // Translate through the ov.* namespace; fall back to the English label when
  // the key is missing (or i18next has no instance yet) so the footer always
  // renders readable text instead of a raw key.
  const tt = (key, fallback) => {
    const resolved = t(ovKey(key), { defaultValue: fallback });
    return !resolved || resolved === ovKey(key) ? fallback : resolved;
  };

  const copyEmail = (email) => {
    try {
      if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(email).catch(() => {});
      }
    } catch (err) {
      /* clipboard unavailable (http / old browser) — the mailto link still works */
    }
    setCopied(email);
    window.setTimeout(() => setCopied(''), 2000);
  };

  const year = new Date().getFullYear();

  const renderLink = (l) => (
    <li key={l.id || l.path}>
      <Link to={l.path}>{tt(`footer.links.${l.id}`, l.name)}</Link>
    </li>
  );

  return (
    <footer className="footer">
      <div className="footer-content">
        <div className="footer-section footer-section--brand">
          <h3>{SITE_NAME}</h3>
          <p className="footer-questions">
            <button
              type="button"
              className="footer-questions__toggle"
              aria-expanded={showEmails}
              aria-controls="footer-emails"
              onClick={() => setShowEmails((v) => !v)}
            >
              {tt('footer.questions', 'Questions? Email us')}
              <span className="footer-questions__chevron" aria-hidden="true">
                {showEmails ? '▴' : '▾'}
              </span>
            </button>
          </p>
          {showEmails && (
            <ul className="footer-emails" id="footer-emails">
              {FOOTER_EMAILS.map((e) => (
                <li key={e.id}>
                  <span className="footer-emails__label">
                    {tt(`footer.emailLabels.${e.id}`, e.label)}
                  </span>
                  <a
                    href={`mailto:${e.email}`}
                    className="footer-emails__address"
                    title={`mailto:${e.email}`}
                    onClick={() => copyEmail(e.email)}
                  >
                    {e.email}
                  </a>
                  {copied === e.email && (
                    <span className="footer-emails__copied">
                      {tt('footer.copied', 'Copied')}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
          {SOCIAL_LINKS.length > 0 && (
            <div className="social-links">
              {SOCIAL_LINKS.map((s) => {
                const Icon = SOCIAL_ICONS[s.label];
                return (
                  <a
                    key={s.label}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.label}
                  >
                    {Icon ? <Icon size={24} /> : s.label}
                  </a>
                );
              })}
            </div>
          )}
        </div>

        {FOOTER_LINKS.company.length > 0 && (
          <div className="footer-section">
            <h3>{tt('footer.company', 'Company')}</h3>
            <ul>{FOOTER_LINKS.company.map(renderLink)}</ul>
          </div>
        )}

        {FOOTER_LINKS.pricing.length > 0 && (
          <div className="footer-section">
            <h3>{tt('footer.pricing', 'Pricing')}</h3>
            <ul>{FOOTER_LINKS.pricing.map(renderLink)}</ul>
          </div>
        )}

        {FOOTER_LINKS.services.length > 0 && (
          <div className="footer-section">
            <h3>{tt('footer.services', 'Services')}</h3>
            <ul>{FOOTER_LINKS.services.map(renderLink)}</ul>
          </div>
        )}
      </div>

      <div className="footer-bottom">
        <p>
          © {year} {SITE_NAME}. {tt('footer.rights', 'All rights reserved.')}
        </p>
      </div>
    </footer>
  );
}
